import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Flex, Link, QueryLoader, toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { useComplianceCaseContext } from "../../../entrypoints/ComplianceCaseContext";
import { AccountTypeEnum, ProfileRelationshipEnum } from "../../../graphql/supergraphEnums";
import {
  useGetProfilesForCaseQuery,
  useBulkUnlinkChildProfilesMutation,
  useUpdateCaseProfileOwnersOrControllersCompletedMutation,
} from "../../../graphql/supergraphHooks";
import { useGuidedProfileCreation } from "../../../hooks/featureFlags";
import {
  isIndividualAccountType,
  isJoinAccountType,
  resolvedAccountType,
} from "../../../utils/account_types";
import { clientContext } from "../../../utils/graphql_utils";
import { AccountTypeFormContent } from "../../AccountTypeForm/AccountTypeFormContent";
import {
  isCategoryChanged,
  getAllAffectedDescendantProfiles,
} from "../../AccountTypeForm/utils";
import { FieldGroup } from "../../common/FieldGroup";
import { provideProfileName } from "../../ProfileTree/utils";
import { useCreateProfile, useUpdateProfile } from "../hooks";
import { DeclarationFormContent } from "./DeclarationFormContent";
import { createDeclarationSchema } from "./schemas";
import { profileDeclarationLink } from "./styles_vanilla.css";
import { DeclarationFormValues } from "./types";
import {
  getDeclarationFormInitialValues,
  prepareIndividualPayload,
  prepareInstitutionPayload,
  prepareJointPayload,
} from "./utils";
import type { ProfileType } from "../../../graphql/supergraphTypes";

export const ProfileDeclaration = ({ controllerMode }: { controllerMode: boolean }) => {
  const history = useHistory();
  const { caseId, arenaId } = useComplianceCaseContext();
  const { profileId, id } = useParams<{ profileId: string; id?: string }>();
  const guidedProfileCreation = useGuidedProfileCreation();

  const [showExtendedTypes, setShowExtendedTypes] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const closePage = () => {
    history.goBack();
  };

  const profileResults = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseId: Number(caseId),
    },
  });

  const complianceCase = profileResults.data?.complianceCase;

  const allProfiles = useMemo(
    () => (complianceCase?.profiles || []).filter((p) => p !== null) as ProfileType[],
    [complianceCase?.profiles],
  );

  const parentProfileId = useMemo(() => id ?? "", [id]);

  const { parentProfile, childProfile, ownershipPercentage } = useMemo(() => {
    const investingProfile = complianceCase?.investingProfile;
    const profiles = complianceCase?.profiles || [];

    const parent =
      profiles?.find((profile) => profile?.id === parseInt(profileId)) || investingProfile;
    const child = id ? profiles?.find((profile) => profile?.id === parseInt(id)) : undefined;
    const relationship = child?.parentRelationships?.find(
      (rel) =>
        rel?.parentProfileId === parseInt(profileId) &&
        rel?.relationshipType === ProfileRelationshipEnum.Owner,
    );

    return {
      parentProfile: parent,
      childProfile: child,
      ownershipPercentage: relationship?.percentOwnership || null,
    };
  }, [complianceCase, profileId, id]);

  const { createIndividual, createInstitution, createJoint, isCreating } = useCreateProfile(
    Number(caseId),
    Number(arenaId),
    Number(profileId),
    controllerMode,
    closePage,
  );

  const { updateIndividual, updateInstitution, updateJoint, isUpdating } = useUpdateProfile(
    Number(id),
    Number(profileId),
    closePage,
  );

  const [bulkUnlinkChildProfiles] = useBulkUnlinkChildProfilesMutation({
    refetchQueries: ["GetProfilesForCase"],
    awaitRefetchQueries: true,
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  const [updateOwnersOrControllersCompleted] =
    useUpdateCaseProfileOwnersOrControllersCompletedMutation({
      refetchQueries: ["GetComplianceCase", "GetProfilesForCase", "GetProfileActionInfo"],
      awaitRefetchQueries: true,
    });

  const isEditMode = useMemo(() => id !== undefined, [id]);

  const pageTitle = useMemo(() => {
    if (isEditMode) {
      if (controllerMode) {
        return "Edit controller";
      }
      return "Edit owner";
    } else {
      if (controllerMode) {
        return "Create new controller";
      }
      return "Create new owner";
    }
  }, [isEditMode, controllerMode]);

  const onlyIndividualAccountType = useMemo(
    () => isJoinAccountType(resolvedAccountType(parentProfile)),
    [parentProfile],
  );

  const showOwnershipPercentage = useMemo(
    () => !controllerMode && !isJoinAccountType(resolvedAccountType(parentProfile)),
    [controllerMode, parentProfile],
  );

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (onlyIndividualAccountType) {
      setCurrentStep(2);
    }
  }, [onlyIndividualAccountType]);

  const handleDeclarationSubmit = async (values: DeclarationFormValues) => {
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    if (isEditMode && childProfile && id && values.accountType) {
      const isCategoryChange = isCategoryChanged(
        resolvedAccountType(childProfile),
        values.accountType,
      );

      const affectedChildren = getAllAffectedDescendantProfiles(allProfiles, id);

      if (isCategoryChange && affectedChildren.length > 0) {
        await bulkUnlinkChildProfiles({
          variables: { profileId: Number(id), caseId: Number(caseId) },
        });
        await updateOwnersOrControllersCompleted({
          variables: {
            caseId: Number(caseId),
            profileId: Number(id),
            ownersCompleted: false,
            controllersCompleted: false,
            documentsCompleted: false,
          },
        });
      }
    }

    if (isIndividualAccountType(values.accountType)) {
      if (isEditMode) {
        await updateIndividual(prepareIndividualPayload(caseId, values, childProfile));
      } else {
        await createIndividual(values);
      }
    } else if (guidedProfileCreation && isJoinAccountType(values.accountType)) {
      if (isEditMode) {
        await updateJoint(prepareJointPayload(caseId, values, childProfile));
      } else {
        await createJoint(values);
      }
    } else {
      if (isEditMode) {
        const isSwitching = values.accountType !== resolvedAccountType(childProfile);
        await updateInstitution(
          prepareInstitutionPayload(caseId, values, isSwitching, childProfile),
        );
      } else {
        await createInstitution(values);
      }
    }
  };

  return (
    <QueryLoader result={profileResults}>
      <Flex flexDirection="column">
        <Link
          to=""
          data-testid="profile-declaration-back-link"
          className={profileDeclarationLink}
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            closePage();
          }}
        >
          {provideProfileName(parentProfile)}
        </Link>
        <FieldGroup title={pageTitle}>
          <Form<DeclarationFormValues>
            key={`declaration-form-${childProfile?.id || "new"}`}
            initialValues={{
              ...getDeclarationFormInitialValues(childProfile, ownershipPercentage),
              accountType:
                resolvedAccountType(childProfile) ||
                (onlyIndividualAccountType ? AccountTypeEnum.Individual : undefined),
            }}
            onSubmit={handleDeclarationSubmit}
            yupValidationSchema={createDeclarationSchema(
              currentStep,
              showOwnershipPercentage,
              parentProfile?.childOwnershipThreshold || 0,
              showExtendedTypes,
            )}
            submitOnlyChangedValues={false}
            validateOnMount={false}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {currentStep === 1 ? (
              <AccountTypeFormContent
                accountName={isEditMode ? provideProfileName(childProfile) : ""}
                isLoading={isSubmitting}
                isControllerMode={controllerMode}
                onExtendedTypesToggle={setShowExtendedTypes}
                profiles={allProfiles}
                profileId={parentProfileId}
              />
            ) : (
              <DeclarationFormContent
                showOwnershipPercentage={showOwnershipPercentage}
                onBack={onlyIndividualAccountType ? undefined : () => setCurrentStep(1)}
                submitting={isSubmitting}
              />
            )}
          </Form>
        </FieldGroup>
      </Flex>
    </QueryLoader>
  );
};
